import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAppStore } from '@/state'
import { useListViews } from '@/services/queries/views'
import { useCADQuery } from '@/services/queries/cads'
import { useListImages } from '@/services/queries/images'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'

import { isUrlExpired } from '../validators/checkLinkExpired'

export const usePreloadImages = () => {
  const serviceWorker = useAppStore((state) => state.serviceWorker)
  const viewUploadsInProgressRef = useRef(
    useAppStore.getState().viewUploadsInProgress,
  )
  useEffect(
    () =>
      useAppStore.subscribe(
        (state) =>
          (viewUploadsInProgressRef.current = state.viewUploadsInProgress),
      ),
    [],
  )

  const { isLoading: isLoadingCadData, data: cadData } = useCADQuery()
  const { isLoading: isDocumentLoading, data: docData } = useDocumentPageQuery()

  const isLoading = useMemo(
    () => isLoadingCadData || isDocumentLoading,
    [isLoadingCadData, isDocumentLoading],
  )

  const cadVersionId = useMemo(() => {
    if (isLoading) return
    return cadData?.version?.id
  }, [isLoading, cadData])

  const { data: views, refetch: refetchViews } = useListViews({
    cadVersionId,
  })
  const { data: images, refetch: refetchImages } = useListImages({
    documentVersionId: docData.documentVersion?.id,
  })

  const preloadAndCacheImage = useCallback(
    async (image) => {
      if (
        image.id &&
        viewUploadsInProgressRef.current
          .map(({ viewId }) => viewId)
          .includes(image.id)
      ) {
        serviceWorker?.deleteCache(
          (image as any).url || (image.download_url as string),
        )
        return false
      }
      try {
        const response = await fetch(image.download_url as string, {
          cache: 'no-cache',
        })
        if (!response.ok) {
          if (response.status === 403 && isUrlExpired(image.download_url)) {
            await refetchViews()
            await refetchImages()
            return true
          }
          throw new Error(`Failed to fetch image: ${image.download_url}`)
        }
        const blob = await response.blob()

        serviceWorker?.conditionallyAddToCache({
          cacheKey: (image as any).url || (image.download_url as string),
          data: {
            body: blob,
            headers: JSON.parse(JSON.stringify(response.headers)),
            ok: response.ok,
            redirected: response.redirected,
            status: response.status,
            statusText: response.statusText,
            type: response.type,
            url: response.url,
          },
        })
        return false
      } catch (error) {
        console.error('Error caching image:', error)
        return false
      }
    },
    [serviceWorker, refetchViews, refetchImages],
  )

  useEffect(() => {
    const preloadAndCacheAllImages = async () => {
      if (
        serviceWorker &&
        ((views && views.length) || (images && images.length))
      ) {
        for (const item of [...(views || []), ...(images || [])]) {
          const shouldStopPreloading = await preloadAndCacheImage(item)
          if (shouldStopPreloading) {
            return
          }
        }
      }
    }

    preloadAndCacheAllImages().catch((error) => {
      console.error('Error preloading images:', error)
    })
  }, [views, images, serviceWorker, preloadAndCacheImage])
}
